import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { customFetcher } from '../../hooks/useFetch';
import { ILegalPhaseFullObject } from '../../Interfaces/ILegalPhase';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { LocationCity, AutoAwesome } from '@mui/icons-material';
import { LegalPhaseRow } from './LegalPhaseRow';
import { FullScreenDialog } from '../../core/FullScreenDialog';
import { LegalPhaseTableEdit } from './Edit/LegalPhaseTableEdit';
import { ILegalRequirementState } from '../../Interfaces/ILegalRequirementState';
import { IDocumentDownloaded } from '../../Interfaces/IDocumentDownloaded';
import { ILegalClient } from '../../Interfaces/ILegalClient';
import {
  useAdministrativeUnits,
  useCourtCourtRoles,
  useFederalStates,
} from '../../Aemter/hooks';
import { LegalPhaseDocumentView } from './LegalPhaseDocumentView';
import { LegalPhaseAddAdminUnit } from './LegalPhaseAddAdminUnit';
import { LegalDependencyMockup } from './LegalDependencyMockup';
import { useLegalDependency } from '../LegalDependencyContext';

interface IProps {
  legalPhaseFullObjectArray: ILegalPhaseFullObject[];
  setLegalPhaseFullObjectArray: Function;
  legalReqState: ILegalRequirementState[];
  downloadedArray?: IDocumentDownloaded[];
  /** Mandanten des Vorgangs — ohne sie bleibt die Spalte "Mandant" in
   *  der Download-Uebersicht leer (QS #16). */
  legalClients?: ILegalClient[];
  legaltransactionId: number;
  startdatedeadline: Date | undefined;
  idAdministrativeUnitTransaction?: number | null;
  onChangeAdministrativeUnitTransaction?: (id: number | null) => void;
}

type View = 'phase_view' | 'document_view';

interface ILegalDependencyRouteState {
  openLegalDependencyEditor?: boolean;
  selectedDependencyRuleId?: number | null;
}

export const LegalPhaseMain: React.FC<IProps> = ({
  legalPhaseFullObjectArray,
  legalReqState,
  legaltransactionId,
  setLegalPhaseFullObjectArray,
  startdatedeadline,
  downloadedArray,
  legalClients,
  idAdministrativeUnitTransaction,
  onChangeAdministrativeUnitTransaction,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { legalDependencyData, setLegalDependencyData } = useLegalDependency();
  // useStates
  const [legalPhaseEditArray, setLegalPhaseEditArray] = useState([
    ...legalPhaseFullObjectArray,
  ]);
  const [isOpenEditDialog, setIsOpenEditDialog] = useState(false);
  const [isOpenAdminUnitDialog, setIsOpenAdminUnitDialog] = useState(false);
  // Regel-Editor, der über "Regel bearbeiten" aus der Abhängigkeits-Übersicht
  // bzw. der Verwendungsliste per Router-State geöffnet wird.
  const [isOpenDependenciesDialog, setIsOpenDependenciesDialog] =
    useState(false);
  const [selectedDependencyRuleId, setSelectedDependencyRuleId] = useState<
    number | null
  >(null);

  const dependencyRouteState =
    location.state as ILegalDependencyRouteState | null;

  useEffect(() => {
    if (!dependencyRouteState?.openLegalDependencyEditor) {
      return;
    }

    // Vorausgewählte Regel in den Editor laden und öffnen ...
    setSelectedDependencyRuleId(
      dependencyRouteState.selectedDependencyRuleId ?? null
    );
    setIsOpenDependenciesDialog(true);
    // ... und den Router-State verwerfen, damit ein erneutes Rendern (z. B.
    // nach dem Schließen) den Dialog nicht wieder aufpoppen lässt.
    navigate(location.pathname, { replace: true, state: null });
  }, [dependencyRouteState, location.pathname, navigate]);
  // React-Query
  const { administrativeUnits, isLoadingAdministrativeUnits } =
    useAdministrativeUnits();
  const { federalStates, isLoadingFederalStates } = useFederalStates();
  const { courtCourtRoles, isLoadingCourtCourtRoles } = useCourtCourtRoles();

  const currentTransactionAdminUnit = useMemo(() => {
    if (!administrativeUnits || !idAdministrativeUnitTransaction)
      return undefined;
    return administrativeUnits.find(
      au => au.idAdministrativeUnit === idAdministrativeUnitTransaction
    );
  }, [administrativeUnits, idAdministrativeUnitTransaction]);

  // Enriched AdminUnits = AdminUnits inkl. ihrer aktiven Court+CourtRole-
  // Zuordnungen. Damit kann das Frontend für jede Maßnahme das zuständige
  // Gericht selbst ableiten — ohne Round-Trip ans Backend. Ergebnis: wenn der
  // User die Vorgangs-Gemarkung ändert, sieht er die abgeleiteten Gerichte
  // sofort, noch bevor er speichert.
  interface IEnrichedAU {
    idAdministrativeUnit: number;
    AdministrativeUnit?: string;
    Assignments?: Array<{
      idAssignment?: number;
      idCourt?: number;
      Court?: string;
      idCourtRole?: number;
      CourtRole?: string;
    }>;
  }
  const { data: enrichedAdminUnits } = useQuery<IEnrichedAU[]>({
    refetchOnWindowFocus: false,
    queryKey: ['/administrativeunit/enriched'],
    queryFn: () => customFetcher({ endpoint: '/administrativeunit/enriched' }),
  });

  // Schneller Lookup: (idAdminUnit, idCourtRole) -> Assignment-Treffer
  const assignmentLookup = useMemo(() => {
    const m = new Map<
      string,
      IEnrichedAU['Assignments'] extends (infer A)[] | undefined ? A : never
    >();
    for (const au of enrichedAdminUnits || []) {
      for (const a of au.Assignments || []) {
        if (a.idCourtRole == null) continue;
        m.set(`${au.idAdministrativeUnit}:${a.idCourtRole}`, a);
      }
    }
    return m;
  }, [enrichedAdminUnits]);

  // Derived: gleiches `legalPhaseFullObjectArray`, aber mit lokal neu
  // berechnetem `resolvedCourt` pro Maßnahme. Wird nur für die Anzeige
  // verwendet — der Save-State (legalPhaseFullObjectArray) bleibt unverändert,
  // weil das Backend `resolvedCourt` beim nächsten Lade ohnehin neu setzt.
  const derivedPhaseArray = useMemo(() => {
    if (!enrichedAdminUnits) return legalPhaseFullObjectArray;
    return legalPhaseFullObjectArray.map(phase => {
      const idAdminUnitPhase = phase.idAdministrativeUnit ?? null;
      const idAdminUnitEffective =
        idAdminUnitPhase || idAdministrativeUnitTransaction || null;
      return {
        ...phase,
        idAdministrativeUnitEffective: idAdminUnitEffective ?? undefined,
        LegalRequirementArray: (phase.LegalRequirementArray || []).map(req => {
          const idCourtRole = req.idCourtRole;
          const base = {
            idCourt: null as number | null,
            Court: null as string | null,
            idAssignment: null as number | null,
            idAdministrativeUnit: idAdminUnitEffective ?? null,
            fromPhase: !!idAdminUnitPhase,
          };
          if (!idCourtRole) {
            return {
              ...req,
              resolvedCourt: { ...base, reason: 'no-court-role' as const },
            };
          }
          if (!idAdminUnitEffective) {
            return {
              ...req,
              resolvedCourt: { ...base, reason: 'no-admin-unit' as const },
            };
          }
          const hit = assignmentLookup.get(
            `${idAdminUnitEffective}:${idCourtRole}`
          );
          if (!hit) {
            return {
              ...req,
              resolvedCourt: { ...base, reason: 'no-assignment' as const },
            };
          }
          return {
            ...req,
            resolvedCourt: {
              idCourt: hit.idCourt ?? null,
              Court: hit.Court ?? null,
              idAssignment: hit.idAssignment ?? null,
              idAdministrativeUnit: idAdminUnitEffective,
              fromPhase: !!idAdminUnitPhase,
              reason: 'ok' as const,
            },
          };
        }),
      };
    });
  }, [
    legalPhaseFullObjectArray,
    enrichedAdminUnits,
    assignmentLookup,
    idAdministrativeUnitTransaction,
  ]);

  // Auto-Vorschlag: aus RealEstate (Grundbuchamt) und Firma (Handelsregister)
  const [suggestions, setSuggestions] = useState<
    Array<{
      idAdministrativeUnit: number;
      AdministrativeUnit: string;
      sources: Array<{ source: string; idCourt: number; labels: string[] }>;
    }>
  >([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [suggestionError, setSuggestionError] = useState<string | null>(null);
  const [isOpenSuggestionsDialog, setIsOpenSuggestionsDialog] = useState(false);

  const loadSuggestions = useCallback(async () => {
    setIsLoadingSuggestions(true);
    setSuggestionError(null);
    try {
      const res = (await customFetcher({
        endpoint: `/legaltransaction/${legaltransactionId}/suggest-administrative-unit`,
      })) as { items: typeof suggestions };
      setSuggestions(res.items || []);
    } catch (e) {
      setSuggestionError(
        e instanceof Error
          ? e.message
          : 'Vorschläge konnten nicht geladen werden.'
      );
      setSuggestions([]);
    } finally {
      setIsLoadingSuggestions(false);
    }
  }, [legaltransactionId]);

  const openSuggestionsDialog = useCallback(() => {
    setIsOpenSuggestionsDialog(true);
    void loadSuggestions();
  }, [loadSuggestions]);
  //
  const handleOpenEdit = () => {
    setLegalPhaseEditArray([...legalPhaseFullObjectArray]);
    setIsOpenEditDialog(true);
  };
  const [currentView, setCurrentView] = useState<View>('phase_view');

  if (
    isLoadingAdministrativeUnits ||
    isLoadingFederalStates ||
    isLoadingCourtCourtRoles
  ) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          height: '20vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!administrativeUnits || !federalStates || !courtCourtRoles) {
    return (
      <Box
        sx={{
          m: 2,
        }}
      >
        <Typography>Fehler beim Laden der Daten</Typography>
      </Box>
    );
  }

  return (
    <>
      <FullScreenDialog
        isOpen={isOpenEditDialog}
        onClose={() => setIsOpenEditDialog(false)}
        title='Rechtsgeschäftsart'
        disablePadding={true}
      >
        <LegalPhaseTableEdit
          legalPhaseArray={legalPhaseEditArray}
          setLegalPhaseArray={setLegalPhaseEditArray}
          setLegalPhaseFullObjectArray={setLegalPhaseFullObjectArray}
          setIsOpenEditDialog={setIsOpenEditDialog}
          adminUnitArray={administrativeUnits}
          federalStateArray={federalStates}
          courtcourtroleArray={courtCourtRoles}
          legaltransactionId={legaltransactionId}
          legalRequirementStates={legalReqState}
        />
      </FullScreenDialog>

      <FullScreenDialog
        isOpen={isOpenDependenciesDialog}
        onClose={() => setIsOpenDependenciesDialog(false)}
        title='Abhängigkeiten zwischen Maßnahmen'
        disablePadding={true}
      >
        <LegalDependencyMockup
          currentLegalTransactionId={legaltransactionId}
          currentLegalPhases={legalPhaseFullObjectArray}
          initialSelectedRuleId={selectedDependencyRuleId}
          legalRequirementStates={legalReqState}
          legalDependencyData={legalDependencyData}
          setLegalDependencyData={setLegalDependencyData}
        />
      </FullScreenDialog>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent='space-between'
        spacing={1}
        sx={{ mb: 1 }}
      >
        <Typography variant='h5'>Aktueller Statusverlauf</Typography>
        <Stack direction='row' spacing={1} alignItems='center'>
          {onChangeAdministrativeUnitTransaction &&
            currentTransactionAdminUnit && (
              <Tooltip title='Maßgebliche Gemeinde/Gemarkung des Vorgangs. Wird für die Ableitung des konkreten Gerichts pro Maßnahme verwendet.'>
                <Chip
                  icon={<LocationCity />}
                  label={`Gemarkung: ${currentTransactionAdminUnit.AdministrativeUnit}`}
                  color='primary'
                  variant='filled'
                  onClick={() => setIsOpenAdminUnitDialog(true)}
                  clickable
                />
              </Tooltip>
            )}
          <Tooltip title='Maßnahmen und Phasen anpassen'>
            <Button onClick={handleOpenEdit} variant='outlined'>
              Anpassen
            </Button>
          </Tooltip>
        </Stack>
      </Stack>

      {onChangeAdministrativeUnitTransaction && (
        <LegalPhaseAddAdminUnit
          open={isOpenAdminUnitDialog}
          onClose={() => setIsOpenAdminUnitDialog(false)}
          currentAdminUnit={currentTransactionAdminUnit}
          adminUnitArray={administrativeUnits}
          federalStateArray={federalStates}
          onSave={newUnit => {
            onChangeAdministrativeUnitTransaction(
              newUnit ? newUnit.idAdministrativeUnit : null
            );
          }}
        />
      )}

      <Dialog
        open={isOpenSuggestionsDialog}
        onClose={() => setIsOpenSuggestionsDialog(false)}
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle>
          <Stack direction='row' alignItems='center' spacing={1}>
            <AutoAwesome color='primary' />
            <span>Vorschläge für Gemeinde/Gemarkung</span>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {isLoadingSuggestions ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : suggestionError ? (
            <Alert severity='error'>{suggestionError}</Alert>
          ) : suggestions.length === 0 ? (
            <Alert severity='info'>
              Aus den Beteiligten dieses Vorgangs konnten keine Vorschläge
              abgeleitet werden. Hinterlegen Sie ein Grundstück oder eine Firma
              im Vorgangsmanager, oder wählen Sie die Gemarkung manuell.
            </Alert>
          ) : (
            <List disablePadding>
              {suggestions.map((s, idx) => (
                <React.Fragment key={s.idAdministrativeUnit}>
                  {idx > 0 && <Divider component='li' />}
                  <ListItemButton
                    onClick={() => {
                      onChangeAdministrativeUnitTransaction?.(
                        s.idAdministrativeUnit
                      );
                      setIsOpenSuggestionsDialog(false);
                    }}
                  >
                    <ListItemText
                      primary={s.AdministrativeUnit}
                      secondary={
                        <Stack
                          direction='row'
                          spacing={0.5}
                          flexWrap='wrap'
                          sx={{ mt: 0.5 }}
                        >
                          {s.sources.map((src, i) => (
                            <Chip
                              key={i}
                              size='small'
                              variant='outlined'
                              label={
                                src.source === 'realestate'
                                  ? `Grundstück: ${src.labels.join(', ')}`
                                  : `Firma: ${src.labels.join(', ')}`
                              }
                            />
                          ))}
                        </Stack>
                      }
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                  </ListItemButton>
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsOpenSuggestionsDialog(false)}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ mt: 2, mb: 2 }}>
        <TextField
          label='Ansicht'
          value={currentView}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setCurrentView(e.target.value as View)
          }
          size='small'
          select
        >
          <MenuItem key='phase-view-normal' value='phase_view'>
            Vollständiger Statusverlauf
          </MenuItem>
          <MenuItem key='phase-view-document' value='document_view'>
            Dokumentenansicht
          </MenuItem>
        </TextField>
      </Box>

      {currentView === 'phase_view' ? (
        <Grid container spacing={1}>
          {[...derivedPhaseArray]
            .sort((a, b) => (a.Position < b.Position ? -1 : 1))
            .map(x => (
              <LegalPhaseRow
                key={`idLegalPhase-${x.idLegalPhase}-${x.LegalRequirementArray.length}`}
                legalPhaseFullObject={x}
                legalPhaseFullObjectArray={legalPhaseFullObjectArray}
                setLegalPhaseFullObjectArray={setLegalPhaseFullObjectArray}
                legalReqState={legalReqState}
                downloadedArray={downloadedArray}
                legalClients={legalClients}
                startdatedeadline={startdatedeadline}
              />
            ))}
        </Grid>
      ) : (
        <LegalPhaseDocumentView
          legalPhaseFullObjectArray={derivedPhaseArray}
          setLegalPhaseFullObjectArray={setLegalPhaseFullObjectArray}
        />
      )}
    </>
  );
};
